import { getFirestore, doc, getDoc, collection, query, where, getDocs, addDoc } from "firebase/firestore";

/**
 * Fetches an event by its bookingLinkId
 * @param {string} bookingLinkId - The booking link ID to search for
 * @returns {Promise<{success: boolean, event: Object|null, error: Error|null}>}
 */
export async function getEventByBookingLinkId(bookingLinkId) {
  const db = getFirestore();
  
  try {
    const eventsRef = collection(db, "events");
    const q = query(eventsRef, where("bookingLinkId", "==", bookingLinkId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return {
        success: false,
        event: null,
        error: new Error("Event not found")
      };
    }
    
    const eventDoc = querySnapshot.docs[0];
    const eventData = eventDoc.data();
    
    // Check if event is active
    if (!eventData.isActive) {
      return {
        success: false,
        event: null,
        error: new Error("Event is not active")
      };
    }
    
    // Check if event has ended
    const now = new Date();
    const endDateTime = eventData.endDateTime.toDate();
    
    if (endDateTime < now) {
      return {
        success: false,
        event: null,
        error: new Error("Event has already ended")
      };
    }
    
    return {
      success: true,
      event: {
        id: eventDoc.id,
        ...eventData
      },
      error: null
    };
  } catch (error) {
    console.error("Error fetching event:", error);
    return {
      success: false,
      event: null,
      error: error
    };
  }
}

/**
 * Generates time slots for an event based on its parameters
 * @param {Object} event - The event object with startDateTime, endDateTime, slotDuration, and maxSlots
 * @returns {Array} Array of time slot objects
 */
export function generateEventTimeSlots(event) {
  const slots = [];
  const startTime = event.startDateTime.toDate();
  const endTime = event.endDateTime.toDate();
  const slotDuration = event.slotDuration; // in minutes
  const maxSlots = event.maxSlots;
  
  let currentTime = new Date(startTime);
  
  while (currentTime < endTime && slots.length < maxSlots) {
    const slotEnd = new Date(currentTime.getTime() + slotDuration * 60000);
    
    // Don't create slots that would extend beyond the event end time
    if (slotEnd <= endTime) {
      slots.push({
        id: `event-${event.id}-${currentTime.getTime()}`,
        time: currentTime.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        }),
        startTime: new Date(currentTime),
        endTime: new Date(slotEnd),
        eventId: event.id,
        maxCapacity: 1, // Each slot can have 1 appointment
        currentBookings: 0 // Will be updated when checking capacity
      });
    }
    
    currentTime = new Date(currentTime.getTime() + slotDuration * 60000);
  }
  
  return slots;
}

/**
 * Checks the capacity of each time slot for an event
 * @param {string} eventId - The event ID
 * @param {Array} timeSlots - Array of time slot objects
 * @returns {Promise<Array>} Updated time slots with capacity information
 */
export async function checkEventSlotCapacity(eventId, timeSlots) {
  const db = getFirestore();
  
  try {
    const appointmentsRef = collection(db, "events", eventId, "appointments");
    const appointmentsSnapshot = await getDocs(appointmentsRef);
    
    // Count bookings for each slot
    const slotBookings = {};
    appointmentsSnapshot.forEach(doc => {
      const appointment = doc.data();
      const slotKey = appointment.slotStart.toDate().getTime();
      slotBookings[slotKey] = (slotBookings[slotKey] || 0) + 1;
    });
    
    // Update time slots with capacity information
    return timeSlots.map(slot => {
      const slotKey = slot.startTime.getTime();
      const currentBookings = slotBookings[slotKey] || 0;
      
      return {
        ...slot,
        currentBookings,
        available: currentBookings < slot.maxCapacity
      };
    });
  } catch (error) {
    console.error("Error checking slot capacity:", error);
    // Return slots with default availability if there's an error
    return timeSlots.map(slot => ({
      ...slot,
      currentBookings: 0,
      available: true
    }));
  }
}

/**
 * Books an appointment for a special event
 * @param {string} eventId - The event ID
 * @param {string} eventBookingLinkId - The event booking link ID
 * @param {Object} patientData - The patient data
 * @param {Object} slotData - The selected slot data
 * @returns {Promise<{success: boolean, appointmentId: string|null, error: Error|null}>}
 */
export async function bookEventAppointment(eventId, eventBookingLinkId, patientData, slotData) {
  const db = getFirestore();
  
  try {
    // Create appointment document in the event's appointments subcollection
    const appointmentsRef = collection(db, "events", eventId, "appointments");
    const appointmentData = {
      eventId: eventId,
      eventBookingLinkId: eventBookingLinkId,
      patientName: `${patientData.firstName} ${patientData.lastName}`,
      patientPhone: patientData.phone,
      patientEmail: patientData.email,
      slotStart: slotData.startTime,
      slotEnd: slotData.endTime,
      createdAt: new Date(),
      patientData: patientData // Store full patient data for reference
    };
    
    const docRef = await addDoc(appointmentsRef, appointmentData);
    
    console.log("Event appointment booked successfully with ID:", docRef.id);
    return {
      success: true,
      appointmentId: docRef.id,
      error: null
    };
  } catch (error) {
    console.error("Error booking event appointment:", error);
    return {
      success: false,
      appointmentId: null,
      error: error
    };
  }
}

/**
 * Gets office information for an event
 * @param {string} officeId - The office ID
 * @returns {Promise<{success: boolean, office: Object|null, error: Error|null}>}
 */
export async function getEventOffice(officeId) {
  const db = getFirestore();
  
  try {
    const officeRef = doc(db, "offices", officeId);
    const officeDoc = await getDoc(officeRef);
    
    if (!officeDoc.exists()) {
      return {
        success: false,
        office: null,
        error: new Error("Office not found")
      };
    }
    
    return {
      success: true,
      office: {
        id: officeDoc.id,
        ...officeDoc.data()
      },
      error: null
    };
  } catch (error) {
    console.error("Error fetching office:", error);
    return {
      success: false,
      office: null,
      error: error
    };
  }
}
